import React, { useRef, useState } from 'react'
import { cn } from '../../utils/cn'

interface FileUploadProps {
  label?: string
  accept?: string
  onFileChange?: (file: File | null) => void
  preview?: string | null
  className?: string
  disabled?: boolean
  // Multi-file upload props
  onFileUpload?: (files: File[]) => void
  onFileRemove?: (fileIndex: number) => void
  uploadedFiles?: File[]
  acceptedFormats?: string[]
  maxSize?: number
  multiple?: boolean
  error?: string
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = 'image/*',
  onFileChange,
  preview,
  className,
  disabled = false,
  error,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleFileSelect = (file: File | null) => {
    if (onFileChange) {
      onFileChange(file)
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    handleFileSelect(file)
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    if (!disabled) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)

    if (disabled) return

    const files = event.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        handleFileSelect(file)
      }
    }
  }

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const removeFile = () => {
    if (!disabled) {
      handleFileSelect(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className={cn('w-full', className)}>
      <label className="block text-paragraph-2 font-medium text-olr-dark mb-3">
        {label}
      </label>

      <div
        className={cn(
          'relative border-2 border-dashed rounded-xl transition-all duration-200',
          'bg-gray-50 hover:bg-gray-100',
          !disabled && 'cursor-pointer',
          isDragOver && !error && 'border-olr-primary bg-blue-50',
          !isDragOver && !error && 'border-gray-300',
          error && 'border-red-300 bg-red-50',
          disabled && 'opacity-50 cursor-not-allowed',
          preview ? 'p-4' : 'p-8'
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!preview ? openFileDialog : undefined}
      >
        {preview ? (
          // Vista con imagen cargada
          <div className="relative">
            <img
              src={preview}
              alt={label}
              className={cn(
                'w-full h-32 object-cover rounded-lg border',
                error ? 'border-red-300' : 'border-gray-200'
              )}
            />

            {/* Overlay con botones */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    openFileDialog()
                  }}
                  disabled={disabled}
                  className="p-2 bg-olr-primary text-white rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    removeFile()
                  }}
                  disabled={disabled}
                  className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Vista vacía
          <div className="text-center">
            <div
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4',
                error ? 'bg-red-500' : 'bg-olr-primary'
              )}
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>

            <p
              className={cn(
                'text-paragraph mb-2',
                error ? 'text-red-600' : 'text-olr-body'
              )}
            >
              Arrastra y suelta una imagen aquí
            </p>
            <p
              className={cn(
                'text-paragraph mb-4',
                error ? 'text-red-500' : 'text-gray-500'
              )}
            >
              o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-400">
              Formatos soportados: JPG, PNG, PDF
            </p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="mt-2 flex items-center">
          <svg
            className="w-4 h-4 text-red-500 mr-1 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}
    </div>
  )
}
